import React, { useState, useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import {
    updateMessage,
    fetchNoteEditHistory,
    fetchAssignees,
} from '~/services/campaign';
import { toast } from 'react-hot-toast';
import Toggle from '~/components/Toggle';
import TipTapEditor from '~/components/tiptap/TipTapEditor';
import Select from 'react-select';
import { DropdownIndicatorProps, components } from 'react-select';
import { formatDateIfValid } from '~/utils';
import { useAuth } from '~/context/AuthProvider';

interface Note {
    id: number;
    created_date: string;
    created_by_name: string;
    note: string;
    tags?: string;
    pinned_flag?: boolean;
    todo_flag?: boolean;
    completed_date?: string | null;
    completed_by_name?: string;
    assignment?: string;
    assignee?: string;
}

interface EditHistory {
    id: number;
    note_content: string;
    edited_by_name: string;
    edited_date: string;
}

interface EditNoteModalProps {
    isOpen: boolean;
    note: Note | null;
    campaignId: number;
    onClose: () => void;
    onSave: (updatedNote: Note) => void;
    customUpdateFn?: (noteId: number, payload: any) => Promise<Response>;
    customFetchHistoryFn?: (noteId: number) => Promise<any[]>;
    hidePinToggle?: boolean;
}

const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <svg
                className='h-4 w-4 text-gray-500 dark:text-gray-400'
                aria-hidden='true'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 20 20'
            >
                <path
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'
                />
            </svg>
        </components.DropdownIndicator>
    );
};

const EditNoteModal: React.FC<EditNoteModalProps> = ({
    isOpen,
    note,
    campaignId,
    onClose,
    onSave,
    customUpdateFn,
    customFetchHistoryFn,
    hidePinToggle = false,
}) => {
    const { person } = useAuth();
    const [noteContent, setNoteContent] = useState('');
    const [isPinned, setIsPinned] = useState(false);
    const [isTodo, setIsTodo] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [assignee, setAssignee] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const [assigneeOptions, setAssigneeOptions] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [editHistory, setEditHistory] = useState<EditHistory[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [expandedHistoryId, setExpandedHistoryId] = useState<number | null>(
        null,
    );

    useEffect(() => {
        if (isOpen && note) {
            setNoteContent(note.note);
            setIsPinned(note.pinned_flag || false);
            setIsTodo(note.todo_flag || false);
            setIsCompleted(!!note.completed_date);
            setExpandedHistoryId(null);
            loadEditHistory();
            loadAssigneeOptions();
        }
    }, [isOpen, note?.id]);

    // Set the assignee once options are loaded
    useEffect(() => {
        if (assigneeOptions.length > 0 && note?.assignment) {
            const found = assigneeOptions.find(
                (opt: any) =>
                    opt.value.toString() === note.assignment?.toString(),
            );
            setAssignee(found || null);
        } else if (assigneeOptions.length > 0 && note?.assignee) {
            const found = assigneeOptions.find(
                (opt: any) => opt.label.trim() === note.assignee,
            );
            setAssignee(found || null);
        } else {
            setAssignee(null);
        }
    }, [assigneeOptions, note?.assignment, note?.assignee]);

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27) onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose]);

    const loadAssigneeOptions = async () => {
        try {
            const assignees = await fetchAssignees();
            const mappedAssignees = assignees?.flatMap((group: any[]) => {
                const groupMembers = group[1];
                return groupMembers.map((member: any) => ({
                    value: member[0],
                    label: member[1],
                }));
            });
            setAssigneeOptions(mappedAssignees || []);
        } catch (error) {
            console.error('Error fetching assignees:', error);
        }
    };

    const loadEditHistory = async () => {
        if (!note) return;
        setLoadingHistory(true);
        try {
            const history = customFetchHistoryFn
                ? await customFetchHistoryFn(note.id)
                : await fetchNoteEditHistory(campaignId, note.id);
            setEditHistory(history);
        } catch (error) {
            console.error('Error fetching edit history:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSave = async () => {
        if (!note || isSaving) return;

        if (isTodo && !assignee) {
            toast.error('Assignee is required when marked as To Do');
            return;
        }

        setIsSaving(true);

        try {
            // Only send fields that actually changed to avoid triggering
            // backend validation on unchanged values
            const payload: any = {};

            if (noteContent !== note.note) {
                payload.note = noteContent;
            }
            if (!hidePinToggle && isPinned !== (note.pinned_flag || false)) {
                payload.pinned_flag = isPinned;
            }
            if (isTodo !== (note.todo_flag || false)) {
                payload.todo_flag = isTodo;
            }

            const wasCompleted = !!note.completed_date;
            if (isCompleted !== wasCompleted) {
                payload.completed = isCompleted;
            }

            const currentAssignmentId = note.assignment?.toString() || '';
            const newAssignmentId = assignee?.value?.toString() || '';
            if (newAssignmentId !== currentAssignmentId) {
                if (assignee) {
                    payload.assignee = parseInt(assignee.value);
                } else {
                    payload.assignment = null;
                }
            }

            // If nothing changed, just close
            if (Object.keys(payload).length === 0) {
                onClose();
                return;
            }

            const response = customUpdateFn
                ? await customUpdateFn(note.id, payload)
                : await updateMessage(note.id, payload);

            if (response.ok || response.status === 200) {
                toast.success('Note updated successfully');
                const currentUserName =
                    person?.first_name && person?.last_name
                        ? `${person.first_name} ${person.last_name}`
                        : 'Current User';
                onSave({
                    ...note,
                    note: noteContent,
                    pinned_flag: isPinned,
                    todo_flag: isTodo,
                    completed_date: isCompleted
                        ? note.completed_date || new Date().toISOString()
                        : null,
                    completed_by_name: isCompleted
                        ? note.completed_by_name || currentUserName
                        : '',
                    assignee: assignee?.label || '',
                    assignment: assignee?.value || '',
                });
                onClose();
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage =
                    typeof errorData.detail === 'string'
                        ? errorData.detail
                        : Array.isArray(errorData.detail)
                        ? errorData.detail.join(', ')
                        : 'Failed to update note';
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Error updating note:', error);
            toast.error('Failed to update note');
        } finally {
            setIsSaving(false);
        }
    };

    const toggleHistoryItem = (id: number) => {
        setExpandedHistoryId(expandedHistoryId === id ? null : id);
    };

    if (!isOpen || !note) return null;

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative max-h-[80vh] w-full max-w-2xl rounded-lg bg-white shadow-lg'
            >
                <div className='flex items-center justify-between p-6 pb-4'>
                    <h3 className='text-[28px]/[32px] font-semibold text-gray-900'>
                        Edit Note
                    </h3>
                    <div className='flex items-center gap-4'>
                        {!hidePinToggle && (
                            <div className='flex items-center gap-2'>
                                <Toggle isOn={isPinned} setIsOn={setIsPinned} />
                                <span className='text-sm font-normal'>Pin</span>
                            </div>
                        )}
                        <button
                            className='text-gray-400 transition-colors hover:text-gray-600'
                            onClick={onClose}
                            aria-label='Close modal'
                        >
                            <RxCross2 size={24} />
                        </button>
                    </div>
                </div>

                <div className='max-h-[60vh] overflow-y-auto px-6'>
                    {/* Mark as To Do */}
                    <div className='mb-3 flex items-start'>
                        <input
                            type='checkbox'
                            checked={isTodo}
                            onChange={(e) => setIsTodo(e.target.checked)}
                            className='mr-2 mt-1.5 text-sm'
                            id='edit-todo-flag'
                        />
                        <label className='text-xs' htmlFor='edit-todo-flag'>
                            <span className='block text-base'>
                                Mark as To Do
                            </span>
                            <span className='block'>
                                If selected the message must be assigned to
                                someone
                            </span>
                        </label>
                    </div>

                    {/* To Do Completed */}
                    {isTodo && (
                        <div className='mb-3 flex items-start'>
                            <input
                                type='checkbox'
                                checked={isCompleted}
                                onChange={(e) =>
                                    setIsCompleted(e.target.checked)
                                }
                                className='mr-2 mt-1.5 text-sm'
                                id='edit-todo-completed'
                            />
                            <label
                                className='text-xs'
                                htmlFor='edit-todo-completed'
                            >
                                <span className='block text-base'>
                                    To Do completed
                                </span>
                            </label>
                        </div>
                    )}

                    {/* Assign To */}
                    <div className='mb-3'>
                        <label className='block text-xs font-semibold'>
                            Assign To
                            {isTodo && <span className='text-red'> *</span>}
                        </label>
                        <Select
                            menuPlacement='auto'
                            value={assignee}
                            onChange={(val: any) => setAssignee(val)}
                            placeholder='Select Assignee (Optional)'
                            isClearable
                            options={assigneeOptions}
                            styles={{
                                control: (baseStyles) => ({
                                    ...baseStyles,
                                    borderColor: 'black',
                                    borderRadius: '0.375rem',
                                    backgroundColor: 'white',
                                }),
                                menu: (baseStyles) => ({
                                    ...baseStyles,
                                    zIndex: 9999,
                                }),
                            }}
                            components={{
                                IndicatorSeparator: null,
                                DropdownIndicator,
                            }}
                        />
                    </div>

                    {/* Message */}
                    <div className='mb-3'>
                        <label className='block text-xs font-semibold'>
                            Message
                        </label>
                        <TipTapEditor
                            initialValue={note.note}
                            onChange={setNoteContent}
                        />
                    </div>

                    {/* Edit History */}
                    {editHistory.length > 0 && (
                        <div className='mb-4'>
                            <h4 className='mb-2 text-sm font-semibold text-gray-700'>
                                Edit History
                            </h4>
                            <div className='space-y-1'>
                                {editHistory.map((entry) => (
                                    <div
                                        key={entry.id}
                                        className='rounded border border-gray-200'
                                    >
                                        <button
                                            onClick={() =>
                                                toggleHistoryItem(entry.id)
                                            }
                                            className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50'
                                        >
                                            {expandedHistoryId === entry.id ? (
                                                <FiChevronDown
                                                    size={14}
                                                    className='flex-shrink-0 text-gray-400'
                                                />
                                            ) : (
                                                <FiChevronRight
                                                    size={14}
                                                    className='flex-shrink-0 text-gray-400'
                                                />
                                            )}
                                            <span className='text-gray-500'>
                                                {formatDateIfValid(
                                                    entry.edited_date,
                                                    'MM/dd/yy h:mm a',
                                                )}
                                            </span>
                                            <span className='font-medium text-gray-700'>
                                                Edited by {entry.edited_by_name}
                                            </span>
                                        </button>
                                        {expandedHistoryId === entry.id && (
                                            <div
                                                className='border-t border-gray-200 bg-gray-50 px-3 py-2 text-sm [&_a]:text-primary [&_a]:underline'
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        entry.note_content ||
                                                        '<em class="text-gray-400">Empty</em>',
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className='border-t border-gray-200 p-6'>
                    <div className='flex justify-start gap-3'>
                        <button
                            type='button'
                            onClick={onClose}
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        >
                            Cancel
                        </button>
                        <button
                            type='button'
                            onClick={handleSave}
                            disabled={isSaving}
                            className='rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase text-black transition-colors duration-500 hover:bg-yellow disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditNoteModal;
